import {
  api as coreApi,
  canonicalGroupThreadKey,
  groupIdFromThreadKey as coreGroupIdFromThreadKey,
  groups as coreGroups,
  MessageType,
  MessageTypeHelper,
  normalizeGroupIdAlias,
  resolveGroupKeyCandidates as coreResolveGroupKeyCandidates,
  ThreadType,
  WebSocketEventType,
} from "@/core_bridge/module_api";
import type { Thread } from "@/data/schemas";
import {
  extractContactPublicKeys,
  resolveContactPublicKey,
  serializeContactPublicKeys,
} from "@/data/adapters/contact_keys";
import { db, getGroupKeyWithVersion } from "@/services/module_api";
import { useStore } from "@/state/legacy_module_api";

const GroupEventKinds = {
  USER_ADDED: WebSocketEventType.USER_ADDED,
  THREAD_CREATED: WebSocketEventType.THREAD_CREATED,
  THREAD_UPDATED: WebSocketEventType.THREAD_UPDATED,
  THREAD_MEMBER_ADDED: WebSocketEventType.THREAD_MEMBER_ADDED,
  THREAD_MEMBER_REMOVED: WebSocketEventType.THREAD_MEMBER_REMOVED,
  GROUP_METADATA_VERSION_CHANGED: WebSocketEventType.GROUP_METADATA_VERSION_CHANGED,
  GROUP_KEY_EPOCH_CHANGED: WebSocketEventType.GROUP_KEY_EPOCH_CHANGED,
  GROUP_TOMBSTONE: WebSocketEventType.GROUP_TOMBSTONE,
  GROUP_MEMBER_ADDED: WebSocketEventType.GROUP_MEMBER_ADDED,
  GROUP_MEMBER_REMOVED: WebSocketEventType.GROUP_MEMBER_REMOVED,
  GROUP_MEMBERSHIP_VERSION_CHANGED: WebSocketEventType.GROUP_MEMBERSHIP_VERSION_CHANGED,
  RESYNC_REQUIRED: WebSocketEventType.RESYNC_REQUIRED,
  GROUP_MEMBERSHIP_RESYNC_REQUIRED: WebSocketEventType.GROUP_MEMBERSHIP_RESYNC_REQUIRED,
} as const;

type GroupEventKind = (typeof GroupEventKinds)[keyof typeof GroupEventKinds];

type GroupEventHandleResult = {
  handled: boolean;
  resolved: boolean;
  queued: boolean;
  blockedDmFallback: boolean;
  shouldPersist: boolean;
};

type GroupEventProcessResult = {
  resolved: boolean;
  errorCode?: string;
};

type PendingGroupEventRecord = {
  id: string;
  kind: GroupEventKind;
  payload: Record<string, any>;
  attempt: number;
  nextAtMs: number;
  updatedAtMs: number;
  lastError?: string;
};

type GroupSyncCounters = {
  group_sync_placeholder_created: number;
  group_sync_placeholder_resolved: number;
  group_sync_retry_scheduled: number;
  group_sync_retry_exhausted: number;
  group_sync_decrypt_failed: number;
  group_sync_routed_as_dm_guard_blocked: number;
};

const PENDING_EVENTS_SETTING_KEY = "group_sync_pending_events_v1";
const RETRY_BASE_DELAYS_MS = [500, 1500, 5000, 15000, 30000, 60000];
const MAX_RETRY_ATTEMPTS = RETRY_BASE_DELAYS_MS.length;
const RETRY_JITTER_RATIO = 0.2;

const pendingEvents = new Map<string, PendingGroupEventRecord>();
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

let initPromise: Promise<void> | null = null;

const counters: GroupSyncCounters = {
  group_sync_placeholder_created: 0,
  group_sync_placeholder_resolved: 0,
  group_sync_retry_scheduled: 0,
  group_sync_retry_exhausted: 0,
  group_sync_decrypt_failed: 0,
  group_sync_routed_as_dm_guard_blocked: 0,
};

function incrementCounter(key: keyof GroupSyncCounters) {
  counters[key] += 1;
}

function jitterDelay(baseMs: number): number {
  const spread = Math.floor(baseMs * RETRY_JITTER_RATIO);
  if (spread <= 0) return baseMs;
  const delta = Math.floor(Math.random() * (spread * 2 + 1)) - spread;
  return Math.max(100, baseMs + delta);
}

function isUnknownGroupName(name: string | undefined | null): boolean {
  return String(name || "")
    .trim()
    .toLowerCase()
    .startsWith("unknown group");
}

function isGenericGroupName(name: string | undefined | null): boolean {
  const normalized = String(name || "")
    .trim()
    .toLowerCase();
  return !normalized || normalized === "thread" || normalized.startsWith("unknown group");
}

function normalizeLifecycleGroupName(raw: any): string | undefined {
  const normalized = String(raw ?? "").trim();
  return normalized ? normalized : undefined;
}

function toFiniteNumber(raw: any): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = Number(raw.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function normalizeGroupId(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  return normalizeGroupIdAlias(String(raw)) || undefined;
}

function groupIdFromThreadKey(rawThreadKey: string | undefined | null): string | undefined {
  if (!rawThreadKey) return undefined;
  return coreGroupIdFromThreadKey(String(rawThreadKey)) || undefined;
}

function buildGroupThreadKey(groupId: string): string {
  return canonicalGroupThreadKey(groupId) || `group:${groupId}`;
}

function extractCanonicalGroupId(payload: Record<string, any>): string | undefined {
  const rawGroupId =
    payload.groupId || payload.group_id || payload.groupID || payload.group_id || undefined;
  const normalized = normalizeGroupId(rawGroupId);
  if (normalized) return normalized;

  const threadKey =
    payload.threadKey || payload.thread_key || payload.threadId || payload.thread_id || undefined;
  return groupIdFromThreadKey(threadKey);
}

function normalizeEventType(eventType?: string | null): GroupEventKind | null {
  if (!eventType) return null;
  const lower = eventType.toLowerCase();
  if (lower === GroupEventKinds.USER_ADDED) return GroupEventKinds.USER_ADDED;
  if (lower === GroupEventKinds.THREAD_CREATED) return GroupEventKinds.THREAD_CREATED;
  if (lower === GroupEventKinds.THREAD_UPDATED) return GroupEventKinds.THREAD_UPDATED;
  if (lower === GroupEventKinds.THREAD_MEMBER_ADDED) return GroupEventKinds.THREAD_MEMBER_ADDED;
  if (lower === GroupEventKinds.THREAD_MEMBER_REMOVED) return GroupEventKinds.THREAD_MEMBER_REMOVED;
  if (lower === GroupEventKinds.GROUP_METADATA_VERSION_CHANGED)
    return GroupEventKinds.GROUP_METADATA_VERSION_CHANGED;
  if (lower === GroupEventKinds.GROUP_KEY_EPOCH_CHANGED) return GroupEventKinds.GROUP_KEY_EPOCH_CHANGED;
  if (lower === GroupEventKinds.GROUP_TOMBSTONE) return GroupEventKinds.GROUP_TOMBSTONE;
  if (lower === GroupEventKinds.GROUP_MEMBER_ADDED) return GroupEventKinds.GROUP_MEMBER_ADDED;
  if (lower === GroupEventKinds.GROUP_MEMBER_REMOVED) return GroupEventKinds.GROUP_MEMBER_REMOVED;
  if (lower === GroupEventKinds.GROUP_MEMBERSHIP_VERSION_CHANGED)
    return GroupEventKinds.GROUP_MEMBERSHIP_VERSION_CHANGED;
  if (lower === GroupEventKinds.RESYNC_REQUIRED) return GroupEventKinds.RESYNC_REQUIRED;
  if (lower === GroupEventKinds.GROUP_MEMBERSHIP_RESYNC_REQUIRED)
    return GroupEventKinds.GROUP_MEMBERSHIP_RESYNC_REQUIRED;

  const canonical = MessageTypeHelper.canonicalize(eventType);
  if (canonical === MessageType.USER_ADDED) return GroupEventKinds.USER_ADDED;
  if (canonical === MessageType.THREAD_CREATED) return GroupEventKinds.THREAD_CREATED;
  if (canonical === MessageType.THREAD_UPDATED) return GroupEventKinds.THREAD_UPDATED;
  if (canonical === MessageType.THREAD_MEMBER_ADDED) return GroupEventKinds.THREAD_MEMBER_ADDED;
  if (canonical === MessageType.THREAD_MEMBER_REMOVED) return GroupEventKinds.THREAD_MEMBER_REMOVED;
  if (canonical === "GROUP_METADATA_VERSION_CHANGED")
    return GroupEventKinds.GROUP_METADATA_VERSION_CHANGED;
  if (canonical === "GROUP_KEY_EPOCH_CHANGED") return GroupEventKinds.GROUP_KEY_EPOCH_CHANGED;
  if (canonical === "GROUP_TOMBSTONE") return GroupEventKinds.GROUP_TOMBSTONE;
  if (canonical === "GROUP_MEMBER_ADDED") return GroupEventKinds.GROUP_MEMBER_ADDED;
  if (canonical === "GROUP_MEMBER_REMOVED") return GroupEventKinds.GROUP_MEMBER_REMOVED;
  if (canonical === "GROUP_MEMBERSHIP_VERSION_CHANGED")
    return GroupEventKinds.GROUP_MEMBERSHIP_VERSION_CHANGED;
  if (canonical === "RESYNC_REQUIRED") return GroupEventKinds.RESYNC_REQUIRED;
  if (canonical === "GROUP_MEMBERSHIP_RESYNC_REQUIRED")
    return GroupEventKinds.GROUP_MEMBERSHIP_RESYNC_REQUIRED;
  return null;
}

function normalizeGroupKeys(groupKeys: Record<string, string> | undefined | null): Record<number, string> {
  const normalized: Record<number, string> = {};
  if (!groupKeys) return normalized;
  Object.entries(groupKeys).forEach(([version, key]) => {
    const parsed = Number(version);
    if (!Number.isNaN(parsed)) {
      normalized[parsed] = String(key);
    }
  });
  return normalized;
}

function stableStringify(value: any): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, any>)[key])}`)
    .join(",")}}`;
}

function simpleHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function buildPendingEventId(kind: GroupEventKind, groupId: string, payload: Record<string, any>): string {
  const explicitId =
    payload.id || payload.messageId || payload.message_id || payload.clientMessageId || payload.client_message_id;
  const suffix = explicitId ? String(explicitId) : simpleHash(stableStringify(payload));
  return `${kind}:${groupId.toLowerCase()}:${suffix}`;
}

function clearPendingTimer(id: string) {
  const existing = pendingTimers.get(id);
  if (existing) {
    clearTimeout(existing);
    pendingTimers.delete(id);
  }
}

async function persistPendingEvents() {
  try {
    const data = JSON.stringify(Array.from(pendingEvents.values()));
    await (db as any).setSetting?.(PENDING_EVENTS_SETTING_KEY, data);
  } catch (err) {
    console.warn("[GroupEventSync] failed to persist pending events", err);
  }
}

function schedulePendingEvent(id: string) {
  clearPendingTimer(id);
  const pending = pendingEvents.get(id);
  if (!pending) return;

  const delay = Math.max(0, pending.nextAtMs - Date.now());
  const timeoutId = setTimeout(() => {
    void processPendingEvent(id, "timer", false);
  }, delay);
  pendingTimers.set(id, timeoutId);
}

async function loadPendingEvents() {
  let raw: string | undefined;
  try {
    raw = await (db as any).getSetting?.(PENDING_EVENTS_SETTING_KEY);
  } catch (err) {
    console.warn("[GroupEventSync] failed to load pending events", err);
    return;
  }
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as PendingGroupEventRecord[];
    parsed.forEach((item) => {
      if (!item?.id || !item?.kind || !item?.payload) return;
      pendingEvents.set(item.id, item);
      schedulePendingEvent(item.id);
    });
  } catch (err) {
    console.warn("[GroupEventSync] failed to parse pending events", err);
  }
}

async function removePendingEvent(id: string) {
  clearPendingTimer(id);
  if (pendingEvents.delete(id)) {
    await persistPendingEvents();
  }
}

async function scheduleRetry(
  kind: GroupEventKind,
  payload: Record<string, any>,
  groupId: string,
  previousAttempt: number,
  errorCode: string
) {
  const id = buildPendingEventId(kind, groupId, payload);

  if (previousAttempt >= MAX_RETRY_ATTEMPTS) {
    incrementCounter("group_sync_retry_exhausted");
    await removePendingEvent(id);
    console.warn(`[GroupEventSync] retry exhausted for ${id} (${errorCode})`);
    return;
  }

  const baseDelay = RETRY_BASE_DELAYS_MS[Math.min(previousAttempt, RETRY_BASE_DELAYS_MS.length - 1)];
  const delay = jitterDelay(baseDelay);
  const now = Date.now();
  const record: PendingGroupEventRecord = {
    id,
    kind,
    payload,
    attempt: previousAttempt + 1,
    nextAtMs: now + delay,
    updatedAtMs: now,
    lastError: errorCode,
  };

  pendingEvents.set(id, record);
  schedulePendingEvent(id);
  incrementCounter("group_sync_retry_scheduled");
  await persistPendingEvents();
}

async function resolveGroupKeyCandidates(
  threadKey: string | undefined,
  groupId: string | undefined,
  payloadGroupKey: string | undefined,
  store: any
): Promise<string[]> {
  const groupThreadKey = groupId ? buildGroupThreadKey(groupId) : threadKey;
  const existing = groupThreadKey ? store.threadMap?.[groupThreadKey] : undefined;
  const byGroupId = !existing && groupId ? store.threadMap?.[groupId] : undefined;
  const thread = existing || byGroupId;

  const groupKeys =
    thread?.groupKeys && typeof thread.groupKeys === "object"
      ? (thread.groupKeys as Record<string, string>)
      : {};
  const currentKeyVersion = Number(thread?.currentKeyVersion || 0) || undefined;
  const storedCurrentKey =
    (currentKeyVersion && groupKeys[String(currentKeyVersion)]) || thread?.groupKey || undefined;
  const storedMetadataKey = groupKeys["1"] || undefined;
  const historicalKeys = Object.entries(groupKeys)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, value]) => String(value))
    .filter((value) => value && value !== storedCurrentKey && value !== storedMetadataKey);

  let localCurrentKey: string | undefined;
  if (groupId) {
    const keyInfo = await getGroupKeyWithVersion(groupId);
    if (keyInfo) {
      localCurrentKey = keyInfo[0];
    }
  }

  const communityId = thread?.communityId;
  const communityFallbackKey =
    (communityId && (store.communityMap?.[communityId]?.communityKey || store.communityMap?.[communityId]?.community_key)) ||
    undefined;

  return coreResolveGroupKeyCandidates(
    payloadGroupKey,
    storedCurrentKey || localCurrentKey,
    storedMetadataKey,
    historicalKeys,
    communityFallbackKey
  );
}

async function ensurePlaceholderThread(
  groupId: string,
  groupKey?: string,
  fallbackGroupName?: string,
  fallbackGroupVersion?: number,
  fallbackUpdatedAt?: number
): Promise<void> {
  const store = useStore.getState() as any;
  const threadKey = buildGroupThreadKey(groupId);
  const existing = store.threadMap?.[threadKey] || (await (db as any).getGrp?.(threadKey));
  if (existing) return;

  const now = Date.now();
  const normalizedKey = String(groupKey || "").trim() || undefined;
  const resolvedName = normalizeLifecycleGroupName(fallbackGroupName);
  const resolvedUpdatedAt = toFiniteNumber(fallbackUpdatedAt) ?? now;
  const placeholderThread: any = {
    id: threadKey,
    groupId,
    name: resolvedName || `Unknown Group (${groupId.slice(0, 8)})`,
    description: null,
    type: ThreadType.GROUP,
    members: [],
    unread: 0,
    joined: true,
    isStandaloneGroup: true,
    groupKey: normalizedKey,
    groupKeys: normalizedKey ? ({ 1: normalizedKey } as any) : undefined,
    currentKeyVersion: normalizedKey ? 1 : undefined,
    createdAt: now,
    updatedAt: resolvedUpdatedAt,
    version: toFiniteNumber(fallbackGroupVersion),
  };

  await (db as any).saveThread?.(placeholderThread as Thread);
  store.setGroup?.(placeholderThread as Thread);
  incrementCounter("group_sync_placeholder_created");
}

async function applyLifecycleMetadataFallback(params: {
  groupId: string;
  fallbackGroupName?: string;
  fallbackGroupVersion?: number;
  fallbackUpdatedAt?: number;
}): Promise<void> {
  const store = useStore.getState() as any;
  const threadKey = buildGroupThreadKey(params.groupId);
  const existing = (store.threadMap?.[threadKey] ||
    store.threadMap?.[params.groupId] ||
    (await (db as any).getGrp?.(threadKey))) as Thread | undefined;
  if (!existing) return;

  const fallbackGroupName = normalizeLifecycleGroupName(params.fallbackGroupName);
  const fallbackGroupVersion = toFiniteNumber(params.fallbackGroupVersion);
  const fallbackUpdatedAt = toFiniteNumber(params.fallbackUpdatedAt);

  const next: any = { ...(existing as any) };
  let changed = false;

  if (fallbackGroupName && isGenericGroupName(next.name)) {
    next.name = fallbackGroupName;
    changed = true;
  }

  if (
    fallbackGroupVersion !== undefined &&
    (typeof next.version !== "number" || fallbackGroupVersion > next.version)
  ) {
    next.version = fallbackGroupVersion;
    changed = true;
  }

  if (
    fallbackUpdatedAt !== undefined &&
    (typeof next.updatedAt !== "number" || fallbackUpdatedAt > next.updatedAt)
  ) {
    next.updatedAt = fallbackUpdatedAt;
    changed = true;
  }

  if (!changed) return;
  store.setGroup?.(next as Thread);
  await (db as any).saveThread?.(next as Thread);
}

async function upsertResolvedThread(groupId: string, thread: Thread): Promise<void> {
  const store = useStore.getState() as any;
  const threadKey = buildGroupThreadKey(groupId);
  const existing = store.threadMap?.[threadKey] || store.threadMap?.[groupId];
  const wasUnknown = isUnknownGroupName(existing?.name);
  const isResolved = !isUnknownGroupName((thread as any)?.name);

  store.setGroup?.(thread as Thread);
  await (db as any).saveThread?.(thread as Thread);

  if (wasUnknown && isResolved) {
    incrementCounter("group_sync_placeholder_resolved");
  }
}

async function handleThreadUpdate(payload: Record<string, any>): Promise<GroupEventProcessResult> {
  const encryptedThread = payload.encryptedThread || payload.encrypted_thread;
  const rawThreadKey =
    payload.threadKey || payload.thread_key || payload.threadId || payload.thread_id || encryptedThread?.threadKey;
  const threadKey = typeof rawThreadKey === "string" && rawThreadKey.trim() ? rawThreadKey : undefined;

  const groupId =
    extractCanonicalGroupId(payload) ||
    groupIdFromThreadKey(threadKey) ||
    groupIdFromThreadKey(encryptedThread?.threadKey);

  if (!groupId) {
    return { resolved: false, errorCode: "missing_group_context" };
  }

  const store = useStore.getState() as any;
  const payloadGroupKey = payload.groupKey || payload.group_key;
  const keyCandidates = await resolveGroupKeyCandidates(threadKey, groupId, payloadGroupKey, store);
  const fallbackGroupName = normalizeLifecycleGroupName(payload.groupName || payload.group_name);
  const fallbackGroupVersion = toFiniteNumber(payload.groupVersion ?? payload.group_version);
  const fallbackUpdatedAt = toFiniteNumber(payload.updatedAt ?? payload.updated_at);

  await applyLifecycleMetadataFallback({
    groupId,
    fallbackGroupName,
    fallbackGroupVersion,
    fallbackUpdatedAt,
  });

  if (!keyCandidates.length) {
    await ensurePlaceholderThread(
      groupId,
      undefined,
      fallbackGroupName,
      fallbackGroupVersion,
      fallbackUpdatedAt
    );
    return { resolved: false, errorCode: "missing_group_key" };
  }

  if (encryptedThread) {
    for (const key of keyCandidates) {
      try {
        const updatedThread = coreGroups.decryptThread(encryptedThread, key);
        const normalizedThread = {
          ...(updatedThread as any),
          id: buildGroupThreadKey(groupId),
          groupId,
          type: "GROUP",
        } as Thread;
        await upsertResolvedThread(groupId, normalizedThread);
        return { resolved: true };
      } catch {
        incrementCounter("group_sync_decrypt_failed");
      }
    }
  }

  try {
    const result = await coreApi.getGroup(groupId);
    const group = (result as any)?.group || (result as any)?.thread;
    if (group) {
      const normalizedThread = {
        ...(group as any),
        id: (group as any).id || buildGroupThreadKey(groupId),
        groupId,
        type: "GROUP",
      } as Thread;
      await upsertResolvedThread(groupId, normalizedThread);
      return { resolved: true };
    }
  } catch (err) {
    console.warn("[GroupEventSync] thread_updated fetch failed", err);
  }

  await ensurePlaceholderThread(
    groupId,
    keyCandidates[0],
    fallbackGroupName,
    fallbackGroupVersion,
    fallbackUpdatedAt
  );
  return { resolved: false, errorCode: "group_blob_unavailable" };
}

async function fetchAndStoreContact(userId: string, refresh: boolean) {
  if (!userId) return;
  const store = useStore.getState() as any;
  const existing = store.contactsById?.[userId];
  const existingKeys = extractContactPublicKeys(existing);
  const hasKey = Array.isArray(existingKeys) && existingKeys.some((key: any) => key?.publicKey);
  if (!refresh && existing && hasKey) return;

  try {
    const response = await coreApi.searchUserId(userId);
    const user = response?.user;
    if (!user) return;

    const rawKeys = user.publicKeys || user.public_keys || [];
    const apiDeviceId =
      user.deviceId ||
      user.device_id ||
      (Array.isArray(rawKeys) ? rawKeys[0]?.deviceId || rawKeys[0]?.device_id : undefined) ||
      "default";
    const normalizedKeys = (Array.isArray(rawKeys) ? rawKeys : [])
      .map((entry: any) => {
        const publicKey = entry?.publicKey ?? entry?.public_key;
        if (!publicKey) return null;
        const deviceId = entry?.deviceId ?? entry?.device_id ?? apiDeviceId ?? "default";
        return {
          publicKey: String(publicKey).replace(/\s+/g, ""),
          deviceId,
          mlKemPublicKey: entry?.mlKemPublicKey ?? entry?.ml_kem_public_key ?? undefined,
          signingPublicKey: entry?.signingPublicKey ?? entry?.signing_public_key ?? null,
        };
      })
      .filter(Boolean);
    const apiPublicKey = user.publicKey || user.public_key;
    if (!normalizedKeys.length && apiPublicKey) {
      normalizedKeys.push({
        publicKey: String(apiPublicKey).replace(/\s+/g, ""),
        deviceId: apiDeviceId,
        mlKemPublicKey: undefined,
        signingPublicKey: null,
      });
    }
    const serializedKeys = serializeContactPublicKeys(normalizedKeys, apiDeviceId);
    const normalized = {
      ...user,
      userId: user.userId || user.user_id || userId,
      deviceId: apiDeviceId,
      publicKeys: normalizedKeys,
      publicKey: serializedKeys ?? apiPublicKey ?? null,
      isBlocked: false,
      isMuted: false,
    } as any;

    await db.insertUser(normalized);
    store.setUser?.(normalized);
  } catch (err) {
    console.warn("[GroupEventSync] failed to fetch contact", userId, err);
  }
}

async function handleUserAdded(payload: Record<string, any>): Promise<GroupEventProcessResult> {
  const senderId = payload.senderId || payload.sender_id;
  const groupId = extractCanonicalGroupId(payload);
  if (!senderId || !groupId) {
    return { resolved: false, errorCode: "missing_sender_or_group" };
  }

  const store = useStore.getState() as any;
  const mySecretKey = store.keyPair?.base64?.secretKey || store.keyPair?.base64?.secret_key;

  if (!mySecretKey) {
    return { resolved: false, errorCode: "missing_secret_key" };
  }

  let senderPublicKey = resolveContactPublicKey(store.contactsById?.[senderId]);
  if (!senderPublicKey) {
    await fetchAndStoreContact(senderId, true);
    senderPublicKey = resolveContactPublicKey((useStore.getState() as any).contactsById?.[senderId]);
  }

  if (!senderPublicKey) {
    return { resolved: false, errorCode: "missing_sender_public_key" };
  }

  try {
    const result = coreGroups.processUserAddedMessage(JSON.stringify(payload), mySecretKey, senderPublicKey);

    const normalizedKeys = normalizeGroupKeys(result.groupKeys as any);
    const currentKeyVersion = Number(result.currentKeyVersion || 0) || undefined;
    const currentGroupKey = (currentKeyVersion && normalizedKeys[currentKeyVersion]) || undefined;

    const now = payload.timestamp || Date.now();
    const groupThreadKey = buildGroupThreadKey(result.groupId);
    const thread: any = {
      id: groupThreadKey,
      groupId: result.groupId,
      name: result.groupName,
      description: result.groupDescription,
      type: ThreadType.GROUP,
      groupKeys: normalizedKeys,
      currentKeyVersion,
      members: [],
      isStandaloneGroup: true,
      membershipStatus: {
        status: "active",
        updatedAt: now,
        updatedBy: senderId || null,
      },
      groupKey: currentGroupKey,
      createdAt: now,
      updatedAt: now,
    };

    const existing = store.threadMap?.[groupThreadKey] || store.threadMap?.[result.groupId];
    const merged = existing ? { ...existing, ...thread, updatedAt: now } : thread;
    await upsertResolvedThread(result.groupId, merged as Thread);
    return { resolved: true };
  } catch (err) {
    console.warn("[GroupEventSync] user_added failed", err);
    return { resolved: false, errorCode: "user_added_process_failed" };
  }
}

async function processGroupEvent(kind: GroupEventKind, payload: Record<string, any>): Promise<GroupEventProcessResult> {
  if (kind === GroupEventKinds.USER_ADDED) {
    return handleUserAdded(payload);
  }
  return handleThreadUpdate(payload);
}

function shouldPersistLifecycleEvent(kind: GroupEventKind): boolean {
  if (kind === GroupEventKinds.USER_ADDED) return false;
  if (kind === GroupEventKinds.GROUP_METADATA_VERSION_CHANGED) return false;
  if (kind === GroupEventKinds.GROUP_KEY_EPOCH_CHANGED) return false;
  if (kind === GroupEventKinds.GROUP_TOMBSTONE) return false;
  if (kind === GroupEventKinds.GROUP_MEMBERSHIP_VERSION_CHANGED) return false;
  if (kind === GroupEventKinds.RESYNC_REQUIRED) return false;
  if (kind === GroupEventKinds.GROUP_MEMBERSHIP_RESYNC_REQUIRED) return false;
  return true;
}

async function attemptEventProcessing(
  kind: GroupEventKind,
  payload: Record<string, any>,
  previousAttempt: number
): Promise<GroupEventHandleResult> {
  const groupId = extractCanonicalGroupId(payload);

  const requiresGroupContext =
    kind === GroupEventKinds.THREAD_CREATED ||
    kind === GroupEventKinds.THREAD_UPDATED ||
    kind === GroupEventKinds.THREAD_MEMBER_ADDED ||
    kind === GroupEventKinds.THREAD_MEMBER_REMOVED ||
    kind === GroupEventKinds.GROUP_METADATA_VERSION_CHANGED ||
    kind === GroupEventKinds.GROUP_KEY_EPOCH_CHANGED ||
    kind === GroupEventKinds.GROUP_TOMBSTONE ||
    kind === GroupEventKinds.GROUP_MEMBER_ADDED ||
    kind === GroupEventKinds.GROUP_MEMBER_REMOVED ||
    kind === GroupEventKinds.GROUP_MEMBERSHIP_VERSION_CHANGED ||
    kind === GroupEventKinds.RESYNC_REQUIRED ||
    kind === GroupEventKinds.GROUP_MEMBERSHIP_RESYNC_REQUIRED;

  if (requiresGroupContext && !groupId) {
    incrementCounter("group_sync_routed_as_dm_guard_blocked");
    console.warn(`[GroupEventSync] blocked DM fallback for ${kind}: missing group context`);
    return {
      handled: true,
      resolved: false,
      queued: false,
      blockedDmFallback: true,
      shouldPersist: false,
    };
  }

  if (kind === GroupEventKinds.USER_ADDED && !groupId) {
    return {
      handled: false,
      resolved: false,
      queued: false,
      blockedDmFallback: false,
      shouldPersist: true,
    };
  }

  const processResult = await processGroupEvent(kind, payload);

  if (processResult.resolved) {
    if (groupId) {
      await removePendingEvent(buildPendingEventId(kind, groupId, payload));
    }
    return {
      handled: true,
      resolved: true,
      queued: false,
      blockedDmFallback: false,
      shouldPersist: shouldPersistLifecycleEvent(kind),
    };
  }

  if (groupId) {
    await scheduleRetry(kind, payload, groupId, previousAttempt, processResult.errorCode || "sync_failed");
  }

  return {
    handled: true,
    resolved: false,
    queued: !!groupId,
    blockedDmFallback: false,
    shouldPersist: false,
  };
}

async function processPendingEvent(id: string, trigger: "timer" | "startup" | "reconnect", forceNow: boolean) {
  const pending = pendingEvents.get(id);
  if (!pending) return;

  if (!forceNow && pending.nextAtMs > Date.now()) {
    schedulePendingEvent(id);
    return;
  }

  clearPendingTimer(id);
  const result = await attemptEventProcessing(pending.kind, pending.payload, pending.attempt);
  if (!result.resolved && !result.queued) {
    await removePendingEvent(id);
  }

  if (result.resolved) {
    console.log(`[GroupEventSync] processed pending event ${id} via ${trigger}`);
  }
}

async function processPendingEvents(trigger: "startup" | "reconnect", forceNow: boolean) {
  const ids = Array.from(pendingEvents.keys());
  for (const id of ids) {
    await processPendingEvent(id, trigger, forceNow);
  }
}

async function resolveUnknownPlaceholders(trigger: "startup" | "reconnect") {
  try {
    const threads: Thread[] = (await (db as any).getAllThreads?.()) || [];
    const placeholders = threads.filter((thread: any) => {
      const isGroupType = String(thread?.type || "").toUpperCase() === "GROUP" || String(thread?.id || "").startsWith("group:");
      return isGroupType && isUnknownGroupName(thread?.name);
    });

    for (const thread of placeholders as any[]) {
      const groupId =
        normalizeGroupId(thread.groupId) ||
        groupIdFromThreadKey(thread.id) ||
        normalizeGroupId(thread.id);
      if (!groupId) continue;

      const payload: Record<string, any> = {
        groupId,
        threadKey: buildGroupThreadKey(groupId),
      };
      if (thread.groupKey) {
        payload.groupKey = thread.groupKey;
      }

      await attemptEventProcessing(GroupEventKinds.THREAD_UPDATED, payload, 0);
    }

    if (placeholders.length > 0) {
      console.log(`[GroupEventSync] ${trigger} placeholder sweep processed ${placeholders.length} thread(s)`);
    }
  } catch (err) {
    console.warn("[GroupEventSync] placeholder sweep failed", err);
  }
}

async function ensureInitialized() {
  if (!initPromise) {
    initPromise = (async () => {
      await loadPendingEvents();
      await processPendingEvents("startup", false);
      await resolveUnknownPlaceholders("startup");
    })();
  }
  await initPromise;
}

export const GroupEventSync = {
  handleEvent: async (
    eventType: string,
    payload?: Record<string, any>
  ): Promise<GroupEventHandleResult> => {
    if (!payload) {
      return {
        handled: false,
        resolved: false,
        queued: false,
        blockedDmFallback: false,
        shouldPersist: true,
      };
    }

    await ensureInitialized();

    const normalized = normalizeEventType(eventType);
    if (!normalized) {
      return {
        handled: false,
        resolved: false,
        queued: false,
        blockedDmFallback: false,
        shouldPersist: true,
      };
    }

    return attemptEventProcessing(normalized, payload, 0);
  },

  runStartupSweep: async (): Promise<void> => {
    await ensureInitialized();
    await processPendingEvents("startup", true);
    await resolveUnknownPlaceholders("startup");
  },

  runReconnectSweep: async (): Promise<void> => {
    await ensureInitialized();
    await processPendingEvents("reconnect", true);
    await resolveUnknownPlaceholders("reconnect");
  },

  recordDmFallbackBlocked: async (eventType: string, payload?: Record<string, any>): Promise<void> => {
    await ensureInitialized();
    const normalized = normalizeEventType(eventType);
    if (!normalized) return;
    if (normalized === GroupEventKinds.USER_ADDED) return;
    incrementCounter("group_sync_routed_as_dm_guard_blocked");
    console.warn("[GroupEventSync] blocked DM fallback", { eventType, payload });
  },

  getDebugCounters: (): GroupSyncCounters => ({ ...counters }),
};
